import { PlanType } from 'v2/types'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { pollForPlanUpdate } from './utils'

// What has to happen AFTER a free-trial account's plan-buying payment succeeds on
// a gateway rail, in one place for every such rail.
//
// Two things, and each was lost for a different reason when INR trials moved off
// /change-plan onto a gateway rail (a trial's Add-Card charge, or a saved
// Razorpay card routed to Standard Checkout by overview/recharge-route):
//
//  1. The profile still says free_trial. The plan is flipped by the capture
//     webhook, so nothing in this tab knows yet, and no query refetches it —
//     refreshBillingAfterPayment tracks 'balance' | 'cards' only, and
//     refetchOnWindowFocus is off repo-wide. Left alone, the header badge reads
//     "Free Trial" after a successful upgrade payment, which is exactly the state
//     that makes a customer pay a second time.
//  2. PLAN_UPGRADED never fires. It is emitted from ONE place —
//     useChangePlanMutation.onSuccess (mutations.ts) — which a gateway upgrade no
//     longer calls. That silently zeroed the upgrade funnel for the whole Indian
//     market, and PLAN_UPGRADED is also one of six INTERCOM_EVENTS
//     (utils/analytics.ts), so onboarding automation keyed on it stopped too.
//
// Emitting on the poll's SUCCESS rather than at charge time is deliberate and is
// stricter than the old behaviour: /change-plan fired the event as soon as the API
// accepted the request, whereas this fires only once the profile actually reports
// the new plan. So a lost webhook shows up as a missing event instead of a false
// one.
//
// Budget: must outlast the credit poll it sits behind (refreshBillingAfterPayment
// spends 20s, or 40s on the gateway's "not captured yet" branch), because the plan
// flip is strictly after the credit. 3s x 20 = 60s.
export const TRIAL_PLAN_POLL_INTERVAL_MS = 3000
export const TRIAL_PLAN_POLL_MAX_ATTEMPTS = 20

// Whether a verify result should start the plan follow-up.
//
// VERIFIED is the whole condition — `captured` is deliberately not consulted. The
// plan flip rides on the CAPTURE WEBHOOK, so `captured: false` ("payment processing,
// confirming shortly") is precisely the case that still has an upgrade coming; it is
// the branch that needs the poll most, not the one that can skip it. Both Razorpay
// hosts originally started the follow-up only in the fully-captured `else`, so a
// pending-capture payment left the tab on free_trial and never emitted
// PLAN_UPGRADED.
export const shouldRunTrialFollowUp = (verification: { verified?: boolean }): boolean =>
  verification.verified === true

interface TrialUpgradeFollowUpParams {
  // Whether this payment was a trial's plan purchase. False => no-op, so call
  // sites stay a single unconditional line.
  isTrial: boolean
  refetchProfile: () => Promise<{ data?: { plan?: string } }>
  // Plan before the payment, i.e. the poll's baseline. Defaults to free_trial,
  // which is what a trial account is by definition.
  currentPlan?: string
  amount: number
  currency: string
  // Which rail this came from, so the funnel can tell a card upgrade from a
  // netbanking one. The old event had no such property (there was only one path).
  method?: string
}

// Fire-and-forget on purpose: this outlives the modal that started it — and the
// page, since the customer may navigate away — so it must not own a toast or touch
// component state. Never rejects.
export const runTrialUpgradeFollowUp = ({
  isTrial,
  refetchProfile,
  currentPlan,
  amount,
  currency,
  method,
}: TrialUpgradeFollowUpParams): void => {
  if (!isTrial) return
  void pollForPlanUpdate(refetchProfile, currentPlan || PlanType.FREE_TRIAL, {
    intervalMs: TRIAL_PLAN_POLL_INTERVAL_MS,
    maxAttempts: TRIAL_PLAN_POLL_MAX_ATTEMPTS,
  }).then((upgraded) => {
    if (!upgraded) return
    track(AnalyticsEvents.PLAN_UPGRADED, {
      plan_name: PlanType.PROFESSIONAL,
      previous_plan: currentPlan || PlanType.FREE_TRIAL,
      amount,
      currency,
      ...(method ? { method } : {}),
    })
  })
}
